import { ProjectHeader } from "@/components/projects";
import { SubstitutionPlanContactFormMenu } from "@/forms/substitution-plan-contact-form/substitution-plan-contact-form-menu";
import { client } from "@/hono";
import { useProject } from "@/hooks/use-project";
import { ProjectContactsContext } from "@/sites/projects/project/panels/project-contacts/context";
import { ProjectContactHeader } from "@/sites/projects/project/panels/project-contacts/project-contact-header";
import { Modal } from "@msuite/ui/modal";
import { useQuery } from "@msuite/ui/query";
import { DataTable } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import type { SubstitutionPlanContactNotificationSchema } from "@msuite/utils/schemas";
import { useMemo } from "react";
import { FormProvider, useFormContext } from "react-hook-form";
import { columns } from "./table-config";

/** Props Interface */
interface SubstitutionPlanContactFormProps {
	projectId: string;
}

export function SubstitutionPlanContactForm({ projectId }: SubstitutionPlanContactFormProps) {
	/** Hooks */
	const methods = useFormContext<SubstitutionPlanContactNotificationSchema>();

	/** Query */
	const { project } = useProject({ projectId });
	const {
		data: contacts,
		refetch,
		isLoading,
		queryKey,
	} = useQuery({
		queryKey: ["project-contacts", projectId],
		queryFn: async () => {
			const response = await client.projects[":projectId"].contacts.$get({
				param: { projectId },
			});
			const data = await responseHandler(response);
			return data.contacts;
		},
	});

	const grouped = useMemo(() => {
		const groupedContacts = contacts?.flatMap((contact) => contact._contactMembers);
		return groupedContacts;
	}, [contacts]);

	/** Render */
	return (
		<FormProvider {...methods}>
			<ProjectContactsContext.Provider value={{ projectId }}>
				<Modal.TableWrapper>
					<DataTable
						data={grouped}
						columns={columns}
						isLoading={isLoading}
						refetch={refetch}
						queryKey={queryKey}
						menu={<SubstitutionPlanContactFormMenu projectId={projectId} />}
						persistenceKey="substitution-plan-contact-form-table"
						initialPinningState={{
							start: ["include"],
						}}
						grouping={["contactId"]}
						enableRowSelection={(row) => !!row.original.email}
						enableMultiRowSelection
						initialColumnVisibility={{
							contactId: false,
							contactName: false,
						}}
						renderGroupHeader={(row) => (
							<ProjectContactHeader
								contactId={row.original.contactId}
								projectId={projectId}
								refetch={refetch}
								numberOfItems={row.subRows.length}
							/>
						)}
						slots={{
							"above-search": () => <ProjectHeader project={project} />,
						}}
					/>
				</Modal.TableWrapper>
			</ProjectContactsContext.Provider>
		</FormProvider>
	);
}
